import { PATIENT_AUTH_STORAGE_KEY } from "../../constants/patientAuthStorage";

export const TREATMENT_API_PATH = Object.freeze({
  previewCapabilityResolve: "/treatments/preview-capabilities/resolve/",
  previewCapabilityMatches: "/treatments/preview-capabilities/resolve/matches/",
  combinedCheckout: "/orders/combined-checkout/",
  programPreview: (programId) => `/treatments/programs/${programId}/preview/`,
  programRuntime: (programId) => `/treatments/programs/${programId}/runtime/`,
  customProgramPreview: (customProgramId) =>
    `/treatments/custom-programs/${customProgramId}/preview/`,
  customProgramRuntime: (customProgramId) =>
    `/treatments/custom-programs/${customProgramId}/runtime/`,
  customProgramRecommendations: (customProgramId) =>
    `/treatments/custom-programs/${customProgramId}/recommendations/`,
  programRuntimeSessions: (programId) =>
    `/treatments/programs/${programId}/runtime-sessions/`,
  customProgramRuntimeSessions: (customProgramId) =>
    `/treatments/custom-programs/${customProgramId}/runtime-sessions/`,
  previewRuntimeSessions:
    "/treatments/preview-capabilities/resolve/runtime-sessions/",
  previewRuntimeSessionDetail: (sessionToken) =>
    `/treatments/preview-capabilities/runtime-sessions/${sessionToken}/`,
  previewRuntimeSessionTransition: (sessionToken) =>
    `/treatments/preview-capabilities/runtime-sessions/${sessionToken}/transition/`,
  previewRuntimeSessionLocationValidation: (sessionToken) =>
    `/treatments/preview-capabilities/runtime-sessions/${sessionToken}/location/validate/`,
  previewRuntimeSessionUpload: (sessionToken) =>
    `/treatments/preview-capabilities/runtime-sessions/${sessionToken}/uploads/`,
  previewRuntimeSessionUploadRemoval: (sessionToken) =>
    `/treatments/preview-capabilities/runtime-sessions/${sessionToken}/uploads/remove/`,
  runtimeSessionBindPatient: (sessionToken) =>
    `/treatments/runtime-sessions/${sessionToken}/bind-patient/`,
  runtimeSessionDetail: (sessionToken) =>
    `/treatments/runtime-sessions/${sessionToken}/`,
  runtimeSessionTransition: (sessionToken) =>
    `/treatments/runtime-sessions/${sessionToken}/transition/`,
  runtimeSessionLocationValidation: (sessionToken) =>
    `/treatments/runtime-sessions/${sessionToken}/location/validate/`,
  runtimeSessionUpload: (sessionToken) =>
    `/treatments/runtime-sessions/${sessionToken}/uploads/`,
  runtimeSessionUploadRemoval: (sessionToken) =>
    `/treatments/runtime-sessions/${sessionToken}/uploads/remove/`,
});

export const TREATMENT_API_AUTH_SCHEME = Object.freeze({
  previewCapability: "PreviewCapability",
});

export const TREATMENT_API_DEFAULTS = Object.freeze({
  requestTimeoutMs: 12000,
  uploadTimeoutMs: 30000,
});

export const TREATMENT_SOURCE_KIND = Object.freeze({
  program: "program",
  customProgram: "custom_program",
});

export const TREATMENT_RUNTIME_STATE = Object.freeze({
  routing: "routing",
  potentialMatches: "potential_matches",
  commonScreening: "common_screening",
  treatmentScreening: "treatment_screening",
  labSelection: "lab_selection",
  recommendations: "recommendations",
  consents: "consents",
  shipping: "shipping",
  productSelection: "product_selection",
  checkoutReady: "checkout_ready",
  checkoutConsumed: "checkout_consumed",
  completed: "completed",
  disqualified: "disqualified",
  expired: "expired",
});

export const TREATMENT_RUNTIME_STATE_ORDER = Object.freeze({
  [TREATMENT_RUNTIME_STATE.routing]: 0,
  [TREATMENT_RUNTIME_STATE.commonScreening]: 1,
  [TREATMENT_RUNTIME_STATE.potentialMatches]: 2,
  [TREATMENT_RUNTIME_STATE.treatmentScreening]: 3,
  [TREATMENT_RUNTIME_STATE.labSelection]: 4,
  [TREATMENT_RUNTIME_STATE.recommendations]: 5,
  [TREATMENT_RUNTIME_STATE.consents]: 6,
  [TREATMENT_RUNTIME_STATE.shipping]: 7,
  [TREATMENT_RUNTIME_STATE.productSelection]: 8,
  [TREATMENT_RUNTIME_STATE.checkoutReady]: 9,
});

export const TREATMENT_RUNTIME_ACTION = Object.freeze({
  back: "back",
  answerRouting: "answer_routing",
  selectTreatments: "select_treatments",
  answerCommon: "answer_common",
  answerPrograms: "answer_programs",
  checkpointProgramAnswers: "checkpoint_program_answers",
  checkpointRoutingAnswer: "checkpoint_routing_answer",
  checkpointCommonAnswer: "checkpoint_common_answer",
  acceptConsents: "accept_consents",
  confirmRecommendations: "confirm_recommendations",
  selectLabs: "select_labs",
  continueWithEligiblePrograms: "continue_with_eligible_programs",
  confirmShipping: "confirm_shipping",
  selectProducts: "select_products",
  completePreview: "complete_preview",
});

export const TREATMENT_STORAGE_KEY = Object.freeze({
  runtimePointer: "welliemd_treatment_runtime_pointer_v2",
  // Read only during the one-time pointer migration. New runtime code must not
  // persist authority, answers, identity, or checkout state under these keys.
  runtimeSession: "welliemd_treatment_runtime_session",
  checkoutResume: "welliemd_treatment_checkout_resume",
  previewSession: "welliemd_treatment_preview_session",
  patientAccessToken: PATIENT_AUTH_STORAGE_KEY.runtimeAccessToken,
});

export const TREATMENT_RUNTIME_POINTER_SCHEMA_VERSION = 2;

export const TREATMENT_AUTHORITY_RESPONSE_KEY = Object.freeze({
  recommendedProgramIds: "__treatment_recommended_program_ids",
});

export const TREATMENT_RUNTIME_TERMINAL_STATES = Object.freeze([
  TREATMENT_RUNTIME_STATE.completed,
  TREATMENT_RUNTIME_STATE.checkoutConsumed,
  TREATMENT_RUNTIME_STATE.expired,
]);

export const TREATMENT_RULE_OPERATOR_ALIASES = Object.freeze({
  greater_than: "gt",
  greater_than_or_equal: "gte",
  less_than: "lt",
  less_than_or_equal: "lte",
});

export const TREATMENT_CHECKOUT_OPERATION = Object.freeze({
  recover: "recover",
});

export const TREATMENT_RECOVERY_ACTION = Object.freeze({
  updatePayment: "update_payment_method",
  removeOptional: "remove_optional_items",
  removeTreatment: "remove_treatment",
  reviewCoupon: "review_coupon",
  saveResume: "save_and_resume",
  support: "contact_support",
});

export const TREATMENT_RECOVERY_ACTION_LABEL = Object.freeze({
  [TREATMENT_RECOVERY_ACTION.updatePayment]: "Update payment method",
  [TREATMENT_RECOVERY_ACTION.removeOptional]: "Remove optional item",
  [TREATMENT_RECOVERY_ACTION.reviewCoupon]: "Review discount",
  [TREATMENT_RECOVERY_ACTION.saveResume]: "Save and resume later",
  [TREATMENT_RECOVERY_ACTION.support]: "Contact support",
});
